package org.vreo.devicehub.features.settings.presentation.settings.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Remove
import androidx.compose.material3.FilledTonalIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp

import org.vreo.devicehub.features.core.presentation.roundToDecimals

/**
 * Controller slider composable for settings such as zoom and focus.
 *
 * Provides a main slider for coarse adjustment and [−]/[+] icon buttons
 * for fine-grained stepping (0.5 % of the range per tap).
 *
 * @param label label displayed above the slider
 * @param sliderPositon current value; drives both slider thumb and value field
 * @param sliderMax maximum of the value range
 * @param sliderMin minimum of the value range
 * @param onSliderPositionChange called whenever the position changes (slider drag or step button)
 * @param onDone called when the user commits a value (drag end, Done IME, or step button tap)
 * @param onTextChange called on every keystroke in the value field
 * @param modifier optional modifier for the root column
 * @param roundToDecimals text field round to decimal place value
 */
@Composable
fun ControlSlider(
    label: String,
    sliderPositon: Float,
    sliderMax: Float,
    sliderMin: Float,
    onSliderPositionChange: (Float) -> Unit,
    onDone: () -> Unit,
    onTextChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    roundToDecimals: Int = 10,
) {
    // 0.5 % of the range per tap, with a sensible floor for very small ranges
    val fineStep = ((sliderMax - sliderMin) * 0.005f).coerceAtLeast(0.01f)

    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(2.dp)
    ) {
        // Label + direct-entry text field
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.onSurface,
            )
            OutlinedTextField(
                value = sliderPositon.roundToDecimals(roundToDecimals).toString(),
                onValueChange = { onTextChange(it) },
                singleLine = true,
                textStyle = MaterialTheme.typography.bodyMedium,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Decimal,
                    imeAction = ImeAction.Done,
                ),
                keyboardActions = KeyboardActions(onDone = { onDone() }),
                shape = MaterialTheme.shapes.small,
                modifier = Modifier.width(104.dp),
            )
        }

        // Main slider
        Slider(
            value = sliderPositon,
            valueRange = sliderMin..sliderMax,
            onValueChange = { onSliderPositionChange(it) },
            onValueChangeFinished = { onDone() },
            modifier = Modifier.fillMaxWidth(),
        )

        // Range labels + fine-step buttons
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = sliderMin.roundToDecimals(1).toString(),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                FilledTonalIconButton(
                    onClick = {
                        val next = (sliderPositon - fineStep).coerceAtLeast(sliderMin)
                        onSliderPositionChange(next)
                        onDone()
                    },
                    modifier = Modifier.size(32.dp),
                ) {
                    Icon(
                        imageVector = Icons.Default.Remove,
                        contentDescription = "Decrease by step",
                        modifier = Modifier.size(16.dp),
                    )
                }
                FilledTonalIconButton(
                    onClick = {
                        val next = (sliderPositon + fineStep).coerceAtMost(sliderMax)
                        onSliderPositionChange(next)
                        onDone()
                    },
                    modifier = Modifier.size(32.dp),
                ) {
                    Icon(
                        imageVector = Icons.Default.Add,
                        contentDescription = "Increase by step",
                        modifier = Modifier.size(16.dp),
                    )
                }
            }

            Text(
                text = sliderMax.roundToDecimals(1).toString(),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}


@Preview
@Composable
fun LensControlPreview() {
    MaterialTheme {
        ControlSlider(
            label = "Zoom",
            sliderPositon = 50.0f,
            sliderMax = 100f,
            sliderMin = 0f,
            onSliderPositionChange = {},
            onDone = {},
            onTextChange = {}
        )
    }
}
